'use client';

import React, { useEffect, useRef, useState } from 'react';
import Matter from 'matter-js';
import Image from 'next/image';

// Available icon images - define outside component to avoid re-creating on every render
const iconImages = [
  '/images/circular-icons/01.png',
  '/images/circular-icons/02.png',
  '/images/circular-icons/03.png',
  '/images/circular-icons/04.png',
  // '/images/circular-icons/05.png',
];

/**
 * PhysicsSimulation - Interactive physics simulation using matter.js
 * 
 * Creates 75 circular bodies that progressively fall and stack at the bottom of the page.
 * Initially drops 20 circles, then continues dropping one circle at random intervals (0.1-1.5s)
 * until all 75 are active. Each circle displays one of 5 circular icon images.
 * Users can interact with the circles using mouse drag.
 * 
 * @component
 * @example
 * ```tsx
 * <PhysicsSimulation />
 * ```
 */
export default function PhysicsSimulation() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  const renderRef = useRef<Matter.Render | null>(null);
  const mouseConstraintRef = useRef<Matter.MouseConstraint | null>(null);
  const bodiesRef = useRef<Matter.Body[]>([]);
  const iconIndicesRef = useRef<number[]>([]);
  const iconElementsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const animationFrameRef = useRef<number | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [bodiesReady, setBodiesReady] = useState(false);
  const [activeCircleCount, setActiveCircleCount] = useState(0);
  const dropTimerRef = useRef<NodeJS.Timeout | null>(null);
  const nextDropIndexRef = useRef<number>(20); // Start dropping from index 20 (after initial 20)
  const totalCircles = 75;
  const initialDropCount = 20;

  const radius = 37.5; // Radius of circles (75px diameter)
  // Physics body radius - match the visual size exactly
  const physicsRadius = radius;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !containerRef.current || !canvasRef.current) return;

    // Reset drop index for this simulation run
    nextDropIndexRef.current = initialDropCount;

    const { Engine, Render, Runner, Bodies, World, Mouse, MouseConstraint, Events } = Matter;
    const container = containerRef.current;
    const canvas = canvasRef.current;

    // Get container dimensions
    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight;

    // Set canvas dimensions explicitly
    canvas.width = width;
    canvas.height = height;

    // Create engine with gravity settings
    const engine = Engine.create({
      gravity: {
        x: 0,
        y: 0.6,
        scale: 0.001
      }
    });
    engineRef.current = engine;

    // Create renderer (we'll use it for mouse tracking but not visual rendering)
    const render = Render.create({
      canvas,
      engine,
      options: {
        width,
        height,
        wireframes: false,
        background: 'transparent',
        pixelRatio: Math.min(window.devicePixelRatio, 2),
        showAngleIndicator: false,
        showVelocity: false,
      },
    });
    renderRef.current = render;
    
    // Run the Matter renderer for mouse tracking (but it will be invisible)
    Render.run(render);

    // Create ground at bottom
    const ground = Bodies.rectangle(width / 2, height - 10, width, 20, {
      isStatic: true,
      render: { fillStyle: 'transparent' },
    });

    // Create walls
    const leftWall = Bodies.rectangle(0, height / 2, 20, height, {
      isStatic: true,
      render: { fillStyle: 'transparent' },
    });
    const rightWall = Bodies.rectangle(width, height / 2, 20, height, {
      isStatic: true,
      render: { fillStyle: 'transparent' },
    });

    // Create all 75 circular bodies with random icons (but don't add to world yet)
    const bodies: Matter.Body[] = [];
    const iconIndices: number[] = [];

    for (let i = 0; i < totalCircles; i++) {
      // Random position - start above viewport
      const x = Math.random() * (width - radius * 2) + radius;
      const y = Math.random() * -1000 - radius; // Start above viewport

      // Create circular body - use physicsRadius to match visual size
      const body = Bodies.circle(x, y, physicsRadius, {
        restitution: 0.6, // Bounciness
        friction: 0.3,
        density: 0.001,
        render: {
          fillStyle: 'transparent',
        },
      });

      bodies.push(body);
      
      // Randomly select an icon index
      const iconIndex = Math.floor(Math.random() * iconImages.length);
      iconIndices.push(iconIndex);
    }

    bodiesRef.current = bodies;
    iconIndicesRef.current = iconIndices;
    
    // Force a re-render to show the icons
    setBodiesReady(true);
    
    // Debug: log that bodies are created
    console.log('Physics simulation initialized:', {
      totalBodies: bodies.length,
      initialDrop: initialDropCount,
      width,
      height,
      iconCount: iconImages.length
    });

    // Add only the first 20 bodies to the world initially
    const initialBodies = bodies.slice(0, initialDropCount);
    
    // Add random spin to initial bodies
    initialBodies.forEach((body) => {
      const randomAngularVelocity = (Math.random() - 0.5) * 0.3; // Random spin between -0.15 and 0.15
      Matter.Body.setAngularVelocity(body, randomAngularVelocity);
    });
    
    World.add(engine.world, [ground, leftWall, rightWall, ...initialBodies]);
    setActiveCircleCount(initialDropCount);

    // Create mouse constraint for dragging
    const mouse = Mouse.create(canvas);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: {
          visible: false,
        },
      },
    });
    mouseConstraintRef.current = mouseConstraint;
    World.add(engine.world, mouseConstraint);

    // Keep mouse in sync with render
    const updateMouseOffset = () => {
      Mouse.setOffset(mouse, {
        x: container.getBoundingClientRect().left,
        y: container.getBoundingClientRect().top,
      });
    };
    
    Events.on(render, 'afterRender', updateMouseOffset);
    
    // Also update on mouse move for better responsiveness
    const handleMouseMove = () => updateMouseOffset();
    canvas.addEventListener('mousemove', handleMouseMove);

    // Add mouse interaction - circles react to mouse proximity
    const mousePosition = { x: 0, y: 0 };
    const interactionRadius = 150; // Distance at which circles start reacting
    const repulsionStrength = 0.0008; // Strength of the repulsion force

    const handleMouseMoveInteraction = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mousePosition.x = event.clientX - rect.left;
      mousePosition.y = event.clientY - rect.top;
    };

    canvas.addEventListener('mousemove', handleMouseMoveInteraction);

    // Track recently shot circles to prevent rapid re-triggering
    const recentlyShot = new Set<Matter.Body>();
    
    // Function to shoot a circle in a random upward direction
    const shootCircle = (body: Matter.Body) => {
      if (recentlyShot.has(body)) return;
      
      // Random upward angle - between π (left) and 2π (right), going through 3π/2 (up)
      const angle = Math.PI + Math.random() * Math.PI;
      // Constant force magnitude
      const forceMagnitude = 0.35;
      
      const forceX = Math.cos(angle) * forceMagnitude;
      const forceY = Math.sin(angle) * forceMagnitude;
      
      Matter.Body.applyForce(body, body.position, {
        x: forceX,
        y: forceY,
      });
      
      // Mark as recently shot and remove after delay
      recentlyShot.add(body);
      setTimeout(() => {
        recentlyShot.delete(body);
      }, 500); // 500ms cooldown
    };

    // Apply repulsive forces to circles near the mouse and shoot on close hover
    const applyMouseForces = () => {
      bodiesRef.current.forEach((body) => {
        const dx = body.position.x - mousePosition.x;
        const dy = body.position.y - mousePosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Shoot circle if mouse is very close (within radius)
        if (distance > 0 && distance < radius * 1.5 && !recentlyShot.has(body)) {
          shootCircle(body);
        }
        // Gentle repulsion for nearby circles
        else if (distance > 0 && distance < interactionRadius) {
          // Calculate force strength (stronger when closer)
          const forceStrength = (1 - distance / interactionRadius) * repulsionStrength;
          
          // Normalize direction vector
          const forceX = (dx / distance) * forceStrength;
          const forceY = (dy / distance) * forceStrength;

          // Apply force to the body
          Matter.Body.applyForce(body, body.position, {
            x: forceX,
            y: forceY,
          });
        }
      });
    };

    // Handle click to shoot circles
    const handleCanvasClick = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;
      
      // Find circle at click position
      bodiesRef.current.forEach((body) => {
        const dx = body.position.x - clickX;
        const dy = body.position.y - clickY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < radius) {
          shootCircle(body);
        }
      });
    };

    canvas.addEventListener('click', handleCanvasClick);

    Events.on(engine, 'beforeUpdate', applyMouseForces);

    // Update icon positions directly via DOM for better performance
    const updateIconPositions = () => {
      bodies.forEach((body, index) => {
        const element = iconElementsRef.current.get(index);
        if (element) {
          element.style.left = `${body.position.x - radius}px`;
          element.style.top = `${body.position.y - radius}px`;
          element.style.transform = `rotate(${body.angle}rad)`;
        }
      });
      animationFrameRef.current = requestAnimationFrame(updateIconPositions);
    };

    // Create runner
    const runner = Runner.create();
    Runner.run(runner, engine);

    // Start position updates after a brief delay to ensure bodies are created
    setTimeout(() => {
      animationFrameRef.current = requestAnimationFrame(updateIconPositions);
    }, 100);

    // Function to drop the next circle
    const dropNextCircle = () => {
      const dropIndex = nextDropIndexRef.current;

      if (dropIndex >= totalCircles) {
        return;
      }

      // Get the next body to drop
      const nextBody = bodiesRef.current[dropIndex];
      
      // Reset its position above viewport
      const x = Math.random() * (width - radius * 2) + radius;
      const y = -radius * 2;
      Matter.Body.setPosition(nextBody, { x, y });
      Matter.Body.setVelocity(nextBody, { x: 0, y: 0 });
      Matter.Body.setAngle(nextBody, 0);
      
      // Add random spin
      const randomAngularVelocity = (Math.random() - 0.5) * 0.3; // Random spin between -0.15 and 0.15
      Matter.Body.setAngularVelocity(nextBody, randomAngularVelocity);
      
      // Add to world
      World.add(engine.world, nextBody);
      
      // Increment counters
      nextDropIndexRef.current = dropIndex + 1;
      setActiveCircleCount(dropIndex + 1);

      // Schedule next drop if we haven't reached max
      if (dropIndex + 1 < totalCircles) {
        const nextDelay = Math.random() * (1500 - 100) + 100; // Random between 0.1s and 1.5s
        dropTimerRef.current = setTimeout(dropNextCircle, nextDelay);
      }
    };

    // Start dropping circles after initial drop (give them 2 seconds to settle)
    const initialDropDelay = 2000;
    dropTimerRef.current = setTimeout(dropNextCircle, initialDropDelay);

    // Handle resize
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;

      // Update canvas size
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Update render options
      render.options.width = newWidth;
      render.options.height = newHeight;

      // Update ground position
      Matter.Body.setPosition(ground, { x: newWidth / 2, y: newHeight - 10 });
      Matter.Body.scale(ground, newWidth / width, 1);

      // Update wall positions
      Matter.Body.setPosition(leftWall, { x: 0, y: newHeight / 2 });
      Matter.Body.setPosition(rightWall, { x: newWidth, y: newHeight / 2 });
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousemove', handleMouseMoveInteraction);
      canvas.removeEventListener('click', handleCanvasClick);
      Events.off(render, 'afterRender', updateMouseOffset);
      Events.off(engine, 'beforeUpdate', applyMouseForces);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (dropTimerRef.current) {
        clearTimeout(dropTimerRef.current);
      }
      if (render) {
        Render.stop(render);
      }
      Runner.stop(runner);
      Engine.clear(engine);
      if (render && render.canvas) {
        render.canvas.remove();
      }
      if (render) {
        render.textures = {};
      }
      iconElementsRef.current.clear();
    };
  }, [isMounted]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 w-full h-full overflow-hidden z-0"
      role="presentation"
      aria-label="Interactive physics simulation"
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full absolute inset-0 cursor-pointer"
        style={{ 
          display: 'block', 
          pointerEvents: 'auto', 
          opacity: 0,
          zIndex: 1
        }}
        aria-hidden="true"
      />
      {/* Overlay icons using DOM elements for better rendering */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
        {isMounted && bodiesReady && activeCircleCount > 0 &&
          bodiesRef.current.slice(0, activeCircleCount).map((body, index) => {
            const iconIndex = iconIndicesRef.current[index];
            const iconSrc = iconImages[iconIndex];
            
            return (
              <div
                key={index}
                ref={(el) => {
                  if (el) {
                    iconElementsRef.current.set(index, el);
                  } else {
                    iconElementsRef.current.delete(index);
                  }
                }}
                className="absolute pointer-events-none transition-none"
                style={{
                  left: `${body.position.x - radius}px`,
                  top: `${body.position.y - radius}px`,
                  transform: `rotate(${body.angle}rad)`,
                  width: `${radius * 2}px`,
                  height: `${radius * 2}px`,
                }}
                aria-hidden="true"
              >
                <img
                  src={iconSrc}
                  alt=""
                  width={radius * 2}
                  height={radius * 2}
                  className="w-full h-full"
                  style={{ 
                    display: 'block',
                    objectFit: 'cover',
                    imageRendering: 'auto'
                  }}
                  onError={(e) => {
                    console.error('Failed to load icon:', iconSrc);
                  }}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
}

